import { useMemo } from "react";
import type { StripeAddressElementOptions } from "@stripe/stripe-js";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { getCurrentUserName } from "../../helpers/users";

export const useAddressElementDefaultValues = () => {
  const { currentUser, memberBillingInfo } = usePaywallCheckoutContext();

  return useMemo<StripeAddressElementOptions["defaultValues"]>(() => {
    const defaultValues: StripeAddressElementOptions["defaultValues"] = {};

    // Supplying any `defaultValues` (even just `name`) disables Stripe's IP-based country detection.
    // Only pre-fill when we already know the member's country; otherwise return an empty object so
    // geolocation still works.
    if (!memberBillingInfo?.address_country) {
      return defaultValues;
    }

    // If there is a community member logged in, we prefill the Address Element with their name.
    //
    // Otherwise, we rely on the Address Element to provide our form with the values it requires
    // to sign up the new community member.
    //
    // Our form does not render the name field if the Address Element is rendered.
    defaultValues.name = getCurrentUserName(currentUser);
    defaultValues.address = {
      line1: memberBillingInfo.address_line1,
      line2: memberBillingInfo.address_line2,
      city: memberBillingInfo.address_city,
      state: memberBillingInfo.address_state,
      postal_code: memberBillingInfo.address_postal_code,
      country: memberBillingInfo.address_country,
    };

    return defaultValues;
  }, [currentUser, memberBillingInfo]);
};
